'use client';

// Template gallery for creating an agent from a curated starting point. Each
// template prefills the create form (brief, mode, schedule, tools, memory,
// budget). Pure data lives in packages/core (lib/agent-templates.ts, shared +
// validity-tested); this is just the picker UI.

import { ArrowRight } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AGENT_TEMPLATES, type AgentTemplate } from '@/lib/agent-templates';
import type { AgentDraft } from './types';

/** Build a create-form draft from a template. */
export function draftFromTemplate(t: AgentTemplate): AgentDraft {
  return {
    name: t.name,
    emoji: t.emoji,
    brief: t.brief,
    mode: t.mode,
    schedule: t.schedule ?? '',
    model: '',
    toolAllowlist: [...t.toolAllowlist],
    connectorGrants: [],
    dailyBudgetCredits: t.dailyBudgetCredits,
    maxStepsPerRun: 20,
    memoryAccess: t.memoryAccess,
    workspaceEnabled: false,
    notifyEmail: false,
    notifyEmailAddress: '',
  };
}

export function AgentTemplatePicker({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (template: AgentTemplate) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Start from a template</DialogTitle>
          <DialogDescription>
            Pick a starting point — it prefills the brief, schedule, tools, and
            guardrails. You can tweak everything before creating.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 pt-1">
          {AGENT_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              className="group flex w-full items-center gap-3.5 rounded-xl border border-border/60 px-4 py-3 text-left transition-colors hover:border-border hover:bg-muted/40"
            >
              <div
                className="grid size-10 shrink-0 place-items-center rounded-xl text-lg"
                style={{ backgroundColor: `${t.color}22`, color: t.color }}
              >
                {t.emoji}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">{t.name}</span>
                  <span className="rounded-full bg-muted/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t.mode}
                  </span>
                  <span className="text-[10.5px] text-muted-foreground/70">{t.scheduleLabel}</span>
                </div>
                <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{t.tagline}</p>
              </div>
              <ArrowRight
                weight="bold"
                className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5"
              />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
